import {
	ReactElement,
	useState,
	useEffect
} from "react";
import {
	ApiKeyScopeKind,
	SaveApiKeyModel,
	ApiKeyGridItem,
	CreateApiKeyResult,
	create,
	update,
	getForEditing,
	EditApiKeyModel
} from "client/Api/ApiKeys/apiKeys";
import { useFormHandler, IFormHandler } from "client/Forms";
import {
	Dialog,
	Form,
	FormItem,
	Checkbox,
	ValidationError,
	TextInput,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { createValidationConfiguration, convertServerErrors } from "client/Validation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	doesNotExceedLength,
	hasMinimumEntries
} from "client/Validation/rules";
import { InputValidationResultKind } from "client/Validation/input";
import { HttpRequestError } from "client/Api/httpClient";
import { ClientValidationErrorModel } from "client/Api/common";
import { scopeToDisplayName } from "./Overview";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { generateTrackingId } from "client/Misc/utils";
import { AlertManager, useAlerts} from "client/Hooks";

const validationConfiguration = createValidationConfiguration<SaveApiKeyModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationConfiguration.forCollection("scope")
	.add(
		isNotNullNorUndefined(),
		hasMinimumEntries(1).fixErrorOn("input").validateOn("input"),
	);

export type ConfigureApiKeyDialogProps = {
	existingItemId?: number;
	onAfterSave(item: ApiKeyGridItem): void;
	onClose(): void;
	onRevealApiKey(key: string): void;
};

export const ConfigureApiKeyDialog = (props: ConfigureApiKeyDialogProps): ReactElement => {
	const {
		existingItemId,
		onAfterSave,
		onClose,
		onRevealApiKey
	} = props;

	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [model, setModel] = useState<SaveApiKeyModel>(createNewItem());
	const formHandler: IFormHandler<SaveApiKeyModel> = useFormHandler<SaveApiKeyModel>(
		model,
		setModel,
		validationConfiguration
	);
	const alertManager: AlertManager = useAlerts();

	useEffect(() => {
		async function fetchItem(id: number, signal: AbortSignal): Promise<void> {
			setIsLoading(true);

			try {
				const item: EditApiKeyModel = await getForEditing(id, signal);

				setModel(convertExistingItem(item));
				setIsLoading(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				onClose();
			}
		}

		if (existingItemId) {
			const cancellation = new AbortController();
			fetchItem(existingItemId, cancellation.signal);
			return () => cancellation.abort();
		} else {
			setModel(createNewItem());
			return undefined;
		}

	}, [alertManager, existingItemId, onClose]);

	const saveHandler = async (): Promise<void> => {
		if (isSaving) {
			return;
		}

		setIsSaving(true);

		const validationResult = await formHandler.validation.validate();
		if (validationResult.kind === InputValidationResultKind.Invalid) {
			setIsSaving(false);
			return;
		}

		try {

			let item: ApiKeyGridItem;
			if (existingItemId) {
				item = await update(existingItemId, model);
			} else {
				const result: CreateApiKeyResult = await create(model);
				item = result.item;

				onRevealApiKey(result.key);
			}

			onAfterSave(item);
			onClose();
		} catch (error) {
			let wasHandled: boolean = false;
			if (error instanceof HttpRequestError && error.statusCode === 400) {
				const errors = error.readJson<ClientValidationErrorModel[]>();
				formHandler.validation.setServerErrors(convertServerErrors(errors));

				wasHandled = true;
			}

			if (!wasHandled) {
				console.error(error);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsSaving(false);
		}
	};

	const addScope = (scopeKind: ApiKeyScopeKind): void => {
		setModel(model => {
			if (model.scope.some(s => s === scopeKind)) {
				return model;
			}

			return {
				...model,
				scope: [
					...model.scope,
					scopeKind
				]
			};
		});
	};

	const removeScope = (scopeKind: ApiKeyScopeKind): void => {
		setModel(model => {
			const scopeIndex: number = model.scope.indexOf(scopeKind);
			if (scopeIndex === -1) {
				return model;
			}

			const scopeCopy: ApiKeyScopeKind[] = [...model.scope];
			scopeCopy.splice(scopeIndex, 1);

			return {
				...model,
				scope: scopeCopy
			};
		});
	};

	return (
		<Dialog hasBusyIndicator={isSaving || isLoading} trackingId="configure-api-key-dialog">
			<Dialog.Header
				title={localize(existingItemId ? "_EditApiKey" : "_NewApiKey")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={saveHandler}>
					<FormItem>
						<Checkbox {...formHandler.for("isEnabled").attachToCheckbox()} text={localize("_Enable")} />
						<ValidationError {...formHandler.for("isEnabled").attachToValidationError()} />
					</FormItem>
					<FormItem label={localize("_Name")} isRequired>
						<TextInput {...formHandler.for("name").attachToTextInput()} />
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>
					<FormItem label={localize("_Scope")} isRequired isForGroup>
						<ScopeEditor allScopes={model.scope} addScope={addScope} removeScope={removeScope} trackingId={formHandler.for("scope").reducer.path} />
						<ValidationError {...formHandler.for("scope").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						onClick={saveHandler}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Close")}
						onClick={onClose}
						trackingId="close"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

ConfigureApiKeyDialog.displayName = "ConfigureApiKeyDialog";

type ScopeEditorProps = {
	allScopes: ReadonlyArray<ApiKeyScopeKind>;
	trackingId: string;
	addScope(scopeKind: ApiKeyScopeKind): void;
	removeScope(scopeKind: ApiKeyScopeKind): void;
};

const ScopeEditor = (props: ScopeEditorProps): ReactElement => {
	const {
		allScopes,
		trackingId,
		addScope,
		removeScope
	} = props;

	const renderCheckBox = (scopeKind: ApiKeyScopeKind): ReactElement => {
		const value: boolean = allScopes.some(s => s === scopeKind);

		const changeHandler = (newValue: boolean): void => {
			if (newValue) {
				addScope(scopeKind);
			} else {
				removeScope(scopeKind);
			}
		};

		return (
			<Checkbox
				value={value}
				onChange={changeHandler}
				text={scopeToDisplayName[scopeKind]}
				trackingId={generateTrackingId(trackingId, `|${scopeKind}`)}
			/>
		);
	};

	return (
		<>
			{renderCheckBox(ApiKeyScopeKind.Activity)}
			{renderCheckBox(ApiKeyScopeKind.UserApps)}
			{renderCheckBox(ApiKeyScopeKind.Announcements)}
			{renderCheckBox(ApiKeyScopeKind.Events)}
			{renderCheckBox(ApiKeyScopeKind.AppIcons)}
			{renderCheckBox(ApiKeyScopeKind.IntegrationCredentials)}
		</>
	);
};

ScopeEditor.displayName = "ScopeEditor";

function convertExistingItem(existingItem: EditApiKeyModel): SaveApiKeyModel {
	return {
		isEnabled: existingItem.isEnabled,
		name: existingItem.name,
		scope: existingItem.scope
	};
}

function createNewItem(): SaveApiKeyModel {
	return {
		isEnabled: true,
		name: "",
		scope: []
	};
}